import axios, {
  type AxiosError,
  type AxiosInstance,
  type GenericAbortSignal,
  type InternalAxiosRequestConfig
} from 'axios';

import i18next from '@/i18n';
import { abortControllerManager } from '@/services/helpers/api';
import { getAppNavigate } from '@/services/helpers/navigate-helper';
import { getClientToken, removeClientToken } from '@/services/user-hash';
import { store } from '@/store';

import { CLIENT_ROUTES, getFullClientRoute } from '../../Shared/routes/routes';
import { setClient } from '../client.store';
import { WEBVIEW_SESSION_MARKER } from '../shared/webview/auth';
import {
  type ISsoAuthorizeOptions,
  ssoAuthorize
} from '../shared/webview/base-actions';
import { isInWebView } from '../shared/webview/env';
import type { IUpdateClientInfo } from '../types/api.types';
import type { ILoginClientCredentials } from '../types/client.types';
import type { IClientAdditionalInfo } from '../types/store.types';

export type CustomAxiosError = AxiosError<{
  message?: string;
  statusCode: number;
}>;

// Расширяем InternalAxiosRequestConfig для хранения AbortController
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  __abortController?: AbortController;
}

// Создаем axios instance для client API
const createClientAxiosInstance = (baseURL?: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseURL || process.env.REACT_APP_CLIENT_API_URL
  });

  // Request interceptor: автоматически создает AbortController для каждого запроса
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Если signal уже передан явно, используем его
    if (!config.signal) {
      const controller = abortControllerManager.createController();
      config.signal = controller.signal;
      // Сохраняем controller в config для последующего удаления
      (config as ExtendedAxiosRequestConfig).__abortController = controller;
    }
    return config;
  });

  // Response interceptor: удаляет AbortController после завершения запроса
  instance.interceptors.response.use(
    (response) => {
      const controller = (response.config as ExtendedAxiosRequestConfig)
        .__abortController;
      if (controller) {
        abortControllerManager.removeController(controller);
      }
      return response;
    },
    (error) => {
      const controller = (error.config as ExtendedAxiosRequestConfig)
        ?.__abortController;
      if (controller) {
        abortControllerManager.removeController(controller);
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Глобальный instance для запросов к client API
const clientAxiosInstance = createClientAxiosInstance();

export class ClientApi {
  ssoJwtToken: string | null = null;

  // Single-flight SSO-авторизации: пачка параллельных запросов, словивших 401,
  // дёргает нативу один раз, а не каждый по отдельности (см. authorizeOnce).
  private ssoAuthorizePromise: ReturnType<typeof ssoAuthorize> | null = null;

  private authorizeOnce(
    options?: ISsoAuthorizeOptions
  ): ReturnType<typeof ssoAuthorize> {
    if (!this.ssoAuthorizePromise) {
      this.ssoAuthorizePromise = ssoAuthorize(options).finally(() => {
        this.ssoAuthorizePromise = null;
      });
    }
    return this.ssoAuthorizePromise;
  }

  isSsoAuthorized(): boolean {
    return !!this.ssoJwtToken;
  }

  /**
   * SSO-авторизация: обменивает суперапп-сессию нативы на JWT и сохраняет его
   * в `ssoJwtToken`, чтобы последующие `call()` уходили уже авторизованными.
   * Можно вызывать проактивно — заранее, в местах, где авторизация точно
   * понадобится, тогда первый запрос не словит 401. Single-flight: параллельные
   * вызовы и ретрай из `call()` ждут один и тот же обмен с нативой.
   *
   * @returns `true`, если токен получен.
   */
  async ssoAuthorize(options?: ISsoAuthorizeOptions): Promise<boolean> {
    const response = await this.authorizeOnce(options);
    if (response) {
      this.ssoJwtToken = response.data.accessToken;
      // Запросы в вебке привязаны на наличие iin после авторизации
      store.dispatch(setClient({ iin: WEBVIEW_SESSION_MARKER }));
      return true;
    }
    return false;
  }

  async call<Request, Response>({
    path,
    method = 'get',
    body,
    params,
    abortSignal,
    baseUrl,
    cancelable = false,
    retried = false
  }: {
    path: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    body?: Request;
    params?: Request;
    abortSignal?: GenericAbortSignal | undefined;
    baseUrl?: string;
    cancelable?: boolean;
    retried?: boolean;
  }): Promise<Response> {
    const clonedParams = structuredClone(params);
    for (const param in clonedParams) {
      if (clonedParams[param] === undefined || !String(clonedParams[param]))
        delete clonedParams[param];
    }

    if (!cancelable && !abortSignal) {
      abortSignal = new AbortController().signal;
    }

    try {
      const client = getClientToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      // В WebView токен приходит через sso/auth (ssoJwtToken) и client-токена
      // может не быть, поэтому гейтим заголовок на сам bearer, а не на client.
      const bearer = isInWebView() ? this.ssoJwtToken : client?.accessToken;
      if (bearer) headers.Authorization = `Bearer ${bearer}`;

      // Используем axios instance вместо прямого вызова axios()
      const axiosInstance = baseUrl
        ? createClientAxiosInstance(baseUrl)
        : clientAxiosInstance;

      const { data } = await axiosInstance({
        url: path,
        method,
        data: body,
        params: clonedParams,
        headers,
        signal: abortSignal // Если передан явно, будет использован вместо автоматического
      });

      return data;
    } catch (e) {
      const error = e as CustomAxiosError;

      if (error?.response?.status === 401) {
        if (!retried && isInWebView()) {
          // Ретраим только один раз: если запрос с обновлённым токеном снова
          // отдал 401, повторная sso-авторизация ничего не изменит — иначе
          // получим бесконечный цикл 401 → ssoAuthorize → call.
          this.ssoJwtToken = null; // протухший токен сбрасываем
          if (await this.ssoAuthorize()) {
            return this.call({
              path,
              method,
              body,
              params,
              abortSignal,
              baseUrl,
              cancelable,
              retried: true
            });
          }
        }

        const navigate = getAppNavigate();
        removeClientToken();
        const newPath = `/${i18next.language}${getFullClientRoute(CLIENT_ROUTES.autoCreditCreate)}`;
        const savedUrl =
          window.location.pathname !== newPath &&
          `${window.location.pathname}${window.location.search}`;
        navigate(newPath, { state: { savedUrl } });
      }

      const statusCode =
        error?.response?.data?.statusCode || error?.response?.status;
      let message = i18next.t('common.error.base-2');
      if (statusCode && statusCode < 500) {
        message = error?.response?.data?.message || message;
      }

      const err = new Error(message);
      // Заглушка, чтобы ошибки с clientApi обрабатывались в handleError
      err.messages = [];
      err.statusCode = statusCode;
      err.isCancelled = axios.isCancel(e);
      throw err;
    }
  }

  authCreateOtp(body: ILoginClientCredentials): Promise<unknown> {
    return this.call({
      path: 'online/auth',
      method: 'post',
      body
    });
  }

  getClientInfo(): Promise<IClientAdditionalInfo> {
    return this.call({ path: 'online/client-info' });
  }

  updateClientInfo(body: IUpdateClientInfo): Promise<IClientAdditionalInfo> {
    return this.call({ path: 'online/client-info', method: 'patch', body });
  }

  getBiometrySessionStatus(
    sessionId: string,
    abortSignal?: GenericAbortSignal
  ): Promise<{ is_validated: boolean; is_processing: boolean }> {
    return this.call({
      path: 'online/biometric-session-status',
      params: { sessionId },
      abortSignal
    });
  }
}

export const clientApi = new ClientApi();
